import { createTheme } from "@mui/material/styles";

export const APPBAR_HEIGHT = 64;

const BRAND = {
  blue:       '#1877F2',
  blueDark:   '#0A5EC0',
  blueLight:  '#E7F0FF',
  blueHover:  'rgba(24,119,242,0.08)',
  bg:         '#F0F2F5',
  surface:    '#FFFFFF',
  border:     'rgba(0,0,0,0.10)',
  text:       '#1C1E21',
  textSub:    '#65676B',
};

const theme = createTheme({
  // ─── Palette ────────────────────────────────────────────────────────────────
  palette: {
    primary: {
      main:          BRAND.blue,
      dark:          BRAND.blueDark,
      light:         BRAND.blueLight,
      contrastText:  '#FFFFFF',
    },
    secondary: {
      main:         '#42B72A',
      dark:         '#2E8A1B',
      light:        '#EBF9E6',
      contrastText: '#FFFFFF',
    },
    error:   { main: '#FA383E' },
    warning: { main: '#E7A500' },
    success: { main: '#2EB82A' },
    info:    { main: BRAND.blue },
    background: {
      default: BRAND.bg,
      paper:   BRAND.surface,
    },
    text: {
      primary:   BRAND.text,
      secondary: BRAND.textSub,
      disabled:  'rgba(0,0,0,0.38)',
    },
    divider: BRAND.border,
    grey: {
      50:  '#F0F2F5',
      100: '#E4E6EA',
      200: '#CCD0D5',
      300: '#BEC3C9',
      400: '#A8B0B9',
      500: '#8D949E',
      600: '#777E8B',
      700: '#606770',
      800: '#444950',
      900: '#1C1E21',
    },
  },

  // ─── Shape ──────────────────────────────────────────────────────────────────
  shape: { borderRadius: 8 },

  // ─── Typography ─────────────────────────────────────────────────────────────
  typography: {
    fontFamily: '"Segoe UI", "Calibri", "Aptos", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.015em' },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { fontWeight: 400, lineHeight: 1.6 },
    body2: { fontWeight: 400, lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
    overline: { fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.7rem' },
  },

  // ─── Breakpoints (unchanged) ─────────────────────────────────────────────────
  breakpoints: {
    values: { xs: 0, sm: 600, md: 1000, lg: 1200, xl: 1536 },
  },

  // ─── Shadows (corporate: subtle, layered) ─────────────────────────────────
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',           // 1
    '0 2px 6px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',           // 2
    '0 4px 12px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.04)',          // 3
    '0 6px 16px rgba(0,0,0,0.10), 0 3px 6px rgba(0,0,0,0.04)',          // 4
    '0 8px 20px rgba(0,0,0,0.10), 0 4px 8px rgba(0,0,0,0.04)',          // 5
    '0 10px 24px rgba(0,0,0,0.10)',                                       // 6
    '0 12px 28px rgba(0,0,0,0.12)',                                       // 7
    '0 14px 32px rgba(0,0,0,0.12)',                                       // 8
    '0 16px 36px rgba(0,0,0,0.12)',                                       // 9
    '0 18px 40px rgba(0,0,0,0.12)',                                       // 10
    '0 20px 44px rgba(0,0,0,0.14)',                                       // 11
    '0 22px 48px rgba(0,0,0,0.14)',                                       // 12
    '0 24px 52px rgba(0,0,0,0.14)',                                       // 13
    '0 26px 56px rgba(0,0,0,0.14)',                                       // 14
    '0 28px 60px rgba(0,0,0,0.14)',                                       // 15
    '0 30px 64px rgba(0,0,0,0.14)',                                       // 16
    '0 32px 68px rgba(0,0,0,0.16)',                                       // 17
    '0 34px 72px rgba(0,0,0,0.16)',                                       // 18
    '0 36px 76px rgba(0,0,0,0.16)',                                       // 19
    '0 38px 80px rgba(0,0,0,0.16)',                                       // 20
    '0 40px 84px rgba(0,0,0,0.18)',                                       // 21
    '0 42px 88px rgba(0,0,0,0.18)',                                       // 22
    '0 44px 92px rgba(0,0,0,0.18)',                                       // 23
    '0 20px 60px rgba(0,0,0,0.20)',                                       // 24
  ],

  // ─── Component overrides ─────────────────────────────────────────────────────
  components: {

    // Global baseline + scrollbar
    MuiCssBaseline: {
      styleOverrides: `
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; -webkit-font-smoothing: antialiased; background-color: #F0F2F5; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(24,119,242,0.30); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(24,119,242,0.55); }
        a { color: #1877F2; text-decoration: none; }
        a:hover { text-decoration: underline; }
      `,
    },

    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          backgroundImage: 'none',
        },
      },
    },

    // Toolbar
    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          minHeight: 64,
        },
      },
    },

    // Button
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          boxShadow: 'none',
          transition: 'all 0.18s ease',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(24,119,242,0.30)',
            transform: 'translateY(-1px)',
          },
          '&:active': { transform: 'translateY(0)', boxShadow: 'none' },
        },
        outlined: {
          borderColor: 'rgba(0,0,0,0.18)',
          '&:hover': { borderColor: '#1877F2', background: 'rgba(24,119,242,0.04)' },
        },
        text: {
          '&:hover': { background: 'rgba(24,119,242,0.06)' },
        },
        sizeLarge: { padding: '10px 24px', fontSize: '0.9375rem' },
        sizeMedium: { padding: '7px 18px' },
        sizeSmall: { padding: '4px 12px', fontSize: '0.8125rem' },
      },
    },

    // Card
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)',
          backgroundImage: 'none',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },

    // Paper
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation0: { boxShadow: 'none' },
        elevation1: { boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' },
        elevation2: { boxShadow: '0 2px 6px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)' },
        elevation3: { boxShadow: '0 4px 12px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.04)' },
        rounded: { borderRadius: 12 },
      },
    },

    // Table — corporate data density
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F0F2F5',
            fontWeight: 700,
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#65676B',
            borderBottom: '2px solid #E4E6EA',
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(24,119,242,0.04)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(24,119,242,0.08)',
            '&:hover': { backgroundColor: 'rgba(24,119,242,0.12)' },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(0,0,0,0.07)',
          padding: '10px 14px',
          fontSize: '0.875rem',
        },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.78rem',
          borderRadius: 6,
        },
        colorPrimary: {
          backgroundColor: '#E7F0FF',
          color: '#1877F2',
        },
      },
    },

    // TextField
    MuiTextField: {
      defaultProps: { autoComplete: 'new-password' },
    },
    MuiOutlinedInput: {
      defaultProps: { autoComplete: 'new-password' },
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FAFBFC',
          transition: 'box-shadow 0.18s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1877F2',
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(24,119,242,0.15)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1877F2',
              borderWidth: 2,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.18)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: '#1877F2' },
        },
      },
    },
    MuiInputBase: {
      defaultProps: { autoComplete: 'new-password' },
    },

    // Sidebar / navigation items
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '1px 0',
          transition: 'background 0.15s ease, color 0.15s ease',
          '&.Mui-selected': {
            backgroundColor: 'rgba(24,119,242,0.10)',
            color: '#1877F2',
            '&:hover': { backgroundColor: 'rgba(24,119,242,0.14)' },
            '& .MuiListItemIcon-root': { color: '#1877F2' },
          },
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        },
      },
    },

    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
        },
      },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(0,0,0,0.08)' },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#1877F2',
            '& + .MuiSwitch-track': { backgroundColor: '#1877F2', opacity: 0.7 },
          },
        },
      },
    },

    // Checkbox
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: '#1877F2' },
        },
      },
    },

    // Radio
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: '#1877F2' },
        },
      },
    },

    // LinearProgress / circular
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: '#E4E6EA' },
        bar:  { borderRadius: 4 },
      },
    },

    // Tooltip — clean dark pill
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1C1E21',
          fontSize: '0.78rem',
          fontWeight: 500,
          borderRadius: 6,
          padding: '5px 10px',
        },
        arrow: { color: '#1C1E21' },
      },
    },

    // Tab
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          '&.Mui-selected': { color: '#1877F2' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#1877F2', height: 3, borderRadius: '3px 3px 0 0' },
      },
    },

    // Snackbar / Alert
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 500 },
      },
    },

    // Dialog
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: '0 24px 64px rgba(0,0,0,0.18)' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: '1.125rem', paddingBottom: 8 },
      },
    },

    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: '#E7F0FF',
          color: '#1877F2',
          fontWeight: 700,
        },
      },
    },

    // Select
    MuiSelect: {
      styleOverrides: {
        icon: { color: '#65676B' },
      },
    },

    // MenuItem
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '1px 4px',
          fontSize: '0.875rem',
          '&.Mui-selected': {
            backgroundColor: 'rgba(24,119,242,0.10)',
            color: '#1877F2',
            '&:hover': { backgroundColor: 'rgba(24,119,242,0.14)' },
          },
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        },
      },
    },

    // Accordion
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 600 },
      },
    },

    // Pagination
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: '#1877F2',
            color: '#fff',
            '&:hover': { backgroundColor: '#0A5EC0' },
          },
        },
      },
    },
  },
});

export default theme;
